import {AttendanceIntent, AttendanceV2Payload, ParsedAttendanceV2Answer} from './attendanceV2Types'

export const ATTENDANCE_JSON_MARKER = '<!--ATTENDANCE_JSON-->'

/** The prose half of an answer: everything before the marker.
 *
 *  Nothing DECIDES on the marker any more — `presentation` does that — but it is still
 *  dual-emitted on real reports for one release, so the text shown to the reader has to be
 *  cleaned of it or the bubble ends in a wall of raw JSON. Delete alongside the marker. */
export function stripAttendanceJsonBlock(text: string): string {
  const idx = (text || '').indexOf(ATTENDANCE_JSON_MARKER)
  if (idx >= 0) return text.slice(0, idx).trim()
  return (text || '').trim()
}

/** Only version and a non-empty kind string are required — the backend introduces new kind
 *  values over time (e.g. `summary`, `list`), and every field below this is read defensively
 *  with optional chaining, so gate-keeping on a fixed allow-list here just drops payloads. */
export function isAttendanceV2Payload(raw: unknown): raw is AttendanceV2Payload {
  if (!raw || typeof raw !== 'object') return false
  const row = raw as Record<string, unknown>
  return row.version === 2 && typeof row.kind === 'string' && row.kind.length > 0
}

type RawRecord = Record<string, unknown>

/** Some backend responses report counts as e.g. `present_days`/`percentage` instead of the
 *  canonical `present`/`presentPercent` fields the rest of the UI reads. Fold them together. */
function normalizeAttendanceSummary(
  summary: RawRecord | undefined,
  data: RawRecord
): RawRecord | undefined {
  if (!summary || typeof summary !== 'object') return summary
  const person = data.person as RawRecord | undefined
  return {
    ...summary,
    present: summary.present ?? summary.present_days,
    absent: summary.absent ?? summary.absent_days,
    late: summary.late ?? summary.late_days,
    leave: summary.leave ?? summary.leave_days,
    bunk: summary.bunk ?? summary.bunk_days,
    other: summary.other ?? summary.other_days,
    total: summary.total ?? summary.marked_days,
    presentPercent: summary.presentPercent ?? summary.percentage,
    personName:
      summary.personName ??
      (typeof person?.studentName === 'string' ? person.studentName : undefined) ??
      (typeof person?.name === 'string' ? person.name : undefined),
    dateFrom: summary.dateFrom ?? data.from,
    dateTo: summary.dateTo ?? data.to,
  }
}

/** True for a day-by-day attendance record (has `date`/`status` per row), as opposed to a
 *  grouped/breakdown row (e.g. per class-section-gender), which the individual-record export
 *  path can't handle. */
function looksLikeIndividualRows(rows: unknown[]): boolean {
  return rows.some((entry) => {
    if (!entry || typeof entry !== 'object') return false
    const row = entry as RawRecord
    return typeof row.date === 'string' && typeof row.status === 'string'
  })
}

function inferIntentFromData(data: RawRecord): AttendanceIntent {
  const grouped = data.grouped
  if (Array.isArray(grouped) && grouped.length > 0) return 'summary'
  const rows = data.rows
  if (Array.isArray(rows) && rows.length > 0) {
    return looksLikeIndividualRows(rows) ? 'individual' : 'ranking'
  }
  return 'summary'
}

/** A `compound` answer nests one mini-payload per sub-request under `parts`, each missing the
 *  `version`/`querySpec` envelope. Lifting them into full payloads here — rather than teaching each
 *  view to read `parts` — is what lets the table, chart and export readers treat a part exactly like
 *  a standalone answer. */
function normalizeCompoundParts(parts: unknown): unknown[] | undefined {
  if (!Array.isArray(parts)) return undefined

  return parts
    .filter((part) => part && typeof part === 'object')
    .map((part, idx) => {
      const record = part as RawRecord
      const normalized = normalizeAttendanceV2Payload({
        ...record,
        version: 2,
        kind: typeof record.kind === 'string' && record.kind ? record.kind : 'result',
      }) as RawRecord
      return {
        ...normalized,
        index: typeof record.index === 'number' ? record.index : idx,
      }
    })
}

/** True once a part carries something worth rendering on its own — data, prose, or a gap notice. */
function partIsRenderable(part: unknown): boolean {
  if (!part || typeof part !== 'object') return false
  const record = part as RawRecord
  return Boolean(record.data || record.narrative || record.gap)
}

/** A multi-part answer the backend flattened instead of labelling `compound`: it merges the
 *  mergeable parts into one `data.rows` array (each row tagged with its `subRequest`) and promotes
 *  one part's `kind` to the top level, while still shipping the un-merged parts under `compound`.
 *  Rendered as-is that becomes a single table whose columns are the union of every part's — mostly
 *  empty cells, no indication which rows answer which sub-request, and any part the backend left
 *  out of the merge (`unmergedIndexes`) missing altogether. So when those parts are present, restore
 *  the `compound` shape and let each one render as its own section. */
function liftMergedCompoundParts(row: RawRecord): void {
  if (Array.isArray(row.parts) && row.parts.length > 0) return

  const compound = row.compound
  if (!compound || typeof compound !== 'object') return

  const rawParts = (compound as RawRecord).parts
  if (!Array.isArray(rawParts) || rawParts.length < 2) return
  if (!rawParts.some(partIsRenderable)) return

  const parts = normalizeCompoundParts(rawParts)
  if (!parts?.length) return

  row.parts = parts
  row.kind = 'compound'
}

type DisambiguationField = 'person' | 'class' | 'branch'

/** Which picker column a candidate belongs to, from its `type` tag. Students/teachers/staff are
 *  people; everything class-, subject- or branch-shaped uses the class option shape — an id and a
 *  label, which is all any of them needs.
 *
 *  `subject` joins the class column rather than getting one of its own: the backend now asks which
 *  subject a name like "Economics (A2 (Com-Q))" meant, and without this the candidates fall through
 *  to `person`, render as nameless people with no id, and reply with the same ambiguous string that
 *  triggered the question. */
function candidateFieldFrom(type: unknown): DisambiguationField {
  const t = typeof type === 'string' ? type.toLowerCase() : ''
  // `grade` belongs here for the same reason `subject` does. Without it, "Show the students with the
  // most absences in Grade AS" — a tenant with an AS grade on two campuses — fell through to the
  // person column, where a grade has no `studentId`, no class and no branch to render: two cards
  // both reading "AS", under a hint offering to look up "a student name or id 4234".
  if (t === 'class' || t === 'section' || t === 'subject' || t === 'grade') return 'class'
  if (t === 'branch' || t === 'campus') return 'branch'
  return 'person'
}

/** The backend moved disambiguation candidates from a top-level `disambiguation: {field, options}`
 *  object into `data.candidates` — a flat list tagged with `type`. Rebuild the shape the picker still
 *  reads so the choices render as selectable cards (carrying `userId` for resolution) instead of a
 *  plain numbered list buried in the narrative. No-op when the backend already sent `disambiguation`
 *  or when there are no candidates. */
function normalizeDisambiguation(row: RawRecord): void {
  if (row.disambiguation && typeof row.disambiguation === 'object') return

  const data = row.data
  const candidates = data && typeof data === 'object' ? (data as RawRecord).candidates : undefined
  if (!Array.isArray(candidates)) return

  const records = candidates.filter(
    (candidate): candidate is RawRecord => Boolean(candidate) && typeof candidate === 'object'
  )
  if (!records.length) return

  const field = candidateFieldFrom(records[0].type)

  const options =
    field === 'person'
      ? records.map((c) => ({
          userId: c.userId,
          personId: c.personId,
          studentId: c.studentId,
          name: c.name,
          class: c.grade ?? c.class ?? c.className,
          grade: c.grade,
          section: c.section,
          branch: c.branch,
          type: c.type,
        }))
      : records.map((c) => ({
          classId: c.classId ?? c.id ?? c.userId,
          className: c.name ?? c.className,
          gradeName: c.grade ?? c.gradeName,
          section: c.section,
          branch: c.branch ?? c.campusName,
          label: c.label,
          // The level ("A Level", "General") and the campus are the whole difference between two
          // same-named grades, so they are carried separately rather than folded into `qualifier`,
          // which a subject already uses for its class.
          levelName: c.levelName,
          // A subject carries its own id and the class it is taught to, which is the only thing
          // telling two same-named subjects apart on screen.
          subjectId: c.subjectId,
          kind: c.kind ?? c.type,
          qualifier: c.className ?? c.levelName,
          // The words that resolve this choice, composed by whoever knew the ids — see the backend's
          // `buildGroupDisambiguationResponse`. Sending back the bare name re-asks the question.
          submitQuery: c.submitQuery,
        }))

  row.disambiguation = {field, options}
}

/** Normalizes payloads that carry attendance data but skip the `querySpec`/canonical
 *  field names the rest of the UI expects (e.g. responses shaped as `data.shape === 'summary'`). */
function normalizeAttendanceV2Payload(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw
  const row = {...(raw as RawRecord)}

  const parts = normalizeCompoundParts(row.parts)
  if (parts) row.parts = parts
  liftMergedCompoundParts(row)
  normalizeDisambiguation(row)

  const data = row.data
  if (data && typeof data === 'object') {
    const dataRecord = data as RawRecord
    row.data = {
      ...dataRecord,
      summary: normalizeAttendanceSummary(dataRecord.summary as RawRecord | undefined, dataRecord),
      // Same `present_days`/`percentage` aliasing applies to the unfiltered counts.
      fullSummary: normalizeAttendanceSummary(
        dataRecord.fullSummary as RawRecord | undefined,
        dataRecord
      ),
    }
  }

  if (!row.querySpec || typeof row.querySpec !== 'object') {
    const dataRecord = (data && typeof data === 'object' ? (data as RawRecord) : {}) as RawRecord
    const person = dataRecord.person as RawRecord | undefined
    row.querySpec = {
      audience: 'student',
      intent: inferIntentFromData(dataRecord),
      // Left undefined on purpose: this envelope is synthesised for payloads that never had a
      // querySpec, and asserting 'day' here made every exported PDF read "Daily attendance"
      // whatever window the answer covered. The real whole-day / per-period distinction is
      // `data.markType`.
      attendanceMode: undefined,
      scope: person ? {resolvedPersons: [person]} : {},
      dateRange: {
        from: typeof dataRecord.from === 'string' ? dataRecord.from : null,
        to: typeof dataRecord.to === 'string' ? dataRecord.to : null,
        source: null,
      },
      output: {},
    }
  }

  return row
}

function extractJsonObject(text: string): unknown | null {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i)
  const candidate = fenced ? fenced[1].trim() : text.trim()
  const firstBrace = candidate.indexOf('{')
  const lastBrace = candidate.lastIndexOf('}')
  if (firstBrace < 0 || lastBrace <= firstBrace) return null
  try {
    return JSON.parse(candidate.slice(firstBrace, lastBrace + 1))
  } catch {
    return null
  }
}

function dedupeConsecutiveNarrativeLines(text: string): string {
  const lines = (text || '').split('\n')
  const result: string[] = []
  let prevTrimmed = ''

  for (const line of lines) {
    const trimmed = line.trim()
    // Table rows are data, not the agent repeating itself: two identical exam rows (same
    // subject, type, date and marks) are two results, and dropping one made the table one
    // short of its "N results" heading (EX-36).
    if (trimmed && trimmed === prevTrimmed && !trimmed.startsWith('|')) continue
    result.push(line)
    prevTrimmed = trimmed
  }

  return result.join('\n').trim()
}

/** The contract's `answer` as it should be shown: marker block removed (dual-emit), and the
 *  line the agent occasionally repeats verbatim collapsed to one. */
export function cleanAttendanceAnswerText(answer: string): string {
  return dedupeConsecutiveNarrativeLines(stripAttendanceJsonBlock(answer))
}

/** Which v2 view a contract `data` block belongs to. The response contract states the
 *  `presentation` rather than the old `kind`, so the discriminator the views branch on is read
 *  back off the payload's own shape — the backend's `kind`, when it still sends one, wins. */
function inferKindFromContractData(data: RawRecord): string {
  const declared = typeof data.kind === 'string' && data.kind.trim() ? data.kind.trim() : null
  if (declared) return declared
  if (Array.isArray(data.parts) && data.parts.length > 0) return 'compound'
  if (Array.isArray(data.candidates) && data.candidates.length > 0) return 'disambiguation'
  if (
    (Array.isArray(data.unmarkedStudents) && data.unmarkedStudents.length > 0) ||
    (Array.isArray(data.incompleteClasses) && data.incompleteClasses.length > 0)
  ) {
    return 'safety_check'
  }
  const hasRollups = Object.values((data.rollups as RawRecord | undefined) ?? {}).some(
    (rows) => Array.isArray(rows) && rows.length > 0
  )
  if (
    hasRollups ||
    (Array.isArray(data.lowestClasses) && data.lowestClasses.length > 0) ||
    (Array.isArray(data.belowThresholdStudents) && data.belowThresholdStudents.length > 0) ||
    (data.comparison && typeof data.comparison === 'object')
  ) {
    return 'comprehensive_report'
  }
  return 'result'
}

/**
 * Builds the payload the v2 views render from the response contract's own `data` block.
 *
 * This is the replacement for marker parsing: `presentation` says whether there is anything to
 * draw and `data` carries it, so nothing here inspects the prose. The normalisation below is
 * the same one the marker path used — field aliasing, compound lifting, candidate rebuilding —
 * because the `data` object itself did not change shape, only how it reaches the portal.
 *
 * Returns `null` when the block is empty or carries nothing renderable, so a `report` turn the
 * backend sent without rows draws prose and stops, exactly as a `text` turn does.
 */
export function buildAttendanceV2PayloadFromData(
  data: unknown,
  narrative: string
): AttendanceV2Payload | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const dataRecord = data as RawRecord
  if (Object.keys(dataRecord).length === 0) return null

  const parsed = normalizeAttendanceV2Payload({
    version: 2,
    kind: inferKindFromContractData(dataRecord),
    narrative,
    data: dataRecord,
  })

  return isAttendanceV2Payload(parsed) ? parsed : null
}

/**
 * @deprecated Pre-contract fallback: reads the payload back out of `<!--ATTENDANCE_JSON-->` in
 * the answer text. Only reached when a response carries no `presentation` at all — the marker
 * is dual-emitted on real reports for exactly one release and then removed, at which point this
 * function and `ATTENDANCE_JSON_MARKER`'s parsing half can go. Never let it decide what to draw
 * on a response that DOES state a presentation: the marker is present on empty answers too,
 * which is the bug the contract exists to close.
 */
export function parseAttendanceV2Answer(answer: string): ParsedAttendanceV2Answer {
  const raw = (answer || '').trim()
  if (!raw) {
    return {narrative: '', payload: null}
  }

  const markerIdx = raw.indexOf(ATTENDANCE_JSON_MARKER)
  if (markerIdx < 0) {
    return {narrative: dedupeConsecutiveNarrativeLines(raw), payload: null}
  }

  const narrative = dedupeConsecutiveNarrativeLines(raw.slice(0, markerIdx).trim())
  const jsonSlice = raw.slice(markerIdx + ATTENDANCE_JSON_MARKER.length).trim()
  const parsed = normalizeAttendanceV2Payload(extractJsonObject(jsonSlice))

  if (!isAttendanceV2Payload(parsed)) {
    return {narrative: dedupeConsecutiveNarrativeLines(narrative || raw), payload: null}
  }

  const resolvedNarrative = dedupeConsecutiveNarrativeLines(
    narrative || parsed.narrative?.trim() || ''
  )

  return {
    narrative: resolvedNarrative,
    payload: parsed,
  }
}
